// Client-side GIF generator using gifenc.
// Supports several visual effects suitable for WhatsApp / social media sharing.
import { GIFEncoder, quantize, applyPalette } from 'gifenc';

/**
 * Loads an image source (dataURL or url) into an HTMLImageElement.
 */
function loadImg(src) {
  return new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = src;
  });
}

/**
 * Returns ImageData from a canvas (Uint8ClampedArray of RGBA).
 */
function getFrameData(ctx, w, h) {
  return ctx.getImageData(0, 0, w, h).data;
}

/**
 * Draws an image scaled and centered to fit inside a canvas (contain mode).
 */
function drawContain(ctx, img, w, h, scale = 1) {
  if (!img) return;
  const ratio = img.width / img.height;
  let drawW, drawH;
  if (ratio > w / h) {
    drawW = w * scale;
    drawH = (w / ratio) * scale;
  } else {
    drawH = h * scale;
    drawW = h * ratio * scale;
  }
  const dx = (w - drawW) / 2;
  const dy = (h - drawH) / 2;
  ctx.drawImage(img, dx, dy, drawW, drawH);
}

/**
 * Wrap text into lines that fit within maxWidth, supporting manual line breaks ("\n" or "|").
 */
function wrapLines(ctx, text, maxWidth, font) {
  ctx.font = font;
  // First respect manual line breaks
  const paragraphs = text.split(/\r?\n|\|/);
  const lines = [];
  for (const para of paragraphs) {
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      continue;
    }
    let current = words[0];
    for (let i = 1; i < words.length; i++) {
      const test = current + ' ' + words[i];
      if (ctx.measureText(test).width <= maxWidth) {
        current = test;
      } else {
        lines.push(current);
        current = words[i];
      }
    }
    lines.push(current);
  }
  return lines;
}

/**
 * Draw text auto-fitting font size and wrapping inside the given box.
 */
function drawFittedText(ctx, text, cx, cy, boxW, boxH, opts = {}) {
  const {
    color = '#FFFFFF',
    strokeColor = '#000000',
    fontFamily = 'Inter, system-ui, sans-serif',
    weight = '900',
    maxFont = 80,
    minFont = 12,
  } = opts;

  if (!text) return;

  let fontSize = maxFont;
  let lines = [];
  // Reduce font size until all lines fit horizontally + vertically
  while (fontSize >= minFont) {
    const font = `${weight} ${fontSize}px ${fontFamily}`;
    lines = wrapLines(ctx, text, boxW, font);
    const lineHeight = fontSize * 1.15;
    const totalH = lines.length * lineHeight;
    if (totalH <= boxH) {
      ctx.font = font;
      const longest = Math.max(...lines.map((l) => ctx.measureText(l).width));
      if (longest <= boxW) break;
    }
    fontSize -= 2;
  }

  const font = `${weight} ${fontSize}px ${fontFamily}`;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const lineHeight = fontSize * 1.15;
  const totalH = lines.length * lineHeight;
  const startY = cy - totalH / 2 + lineHeight / 2;
  const strokeWidth = Math.max(3, Math.round(fontSize * 0.12));

  lines.forEach((line, i) => {
    const y = startY + i * lineHeight;
    if (strokeWidth > 0) {
      ctx.lineWidth = strokeWidth;
      ctx.strokeStyle = strokeColor;
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.strokeText(line, cx, y);
    }
    ctx.fillStyle = color;
    ctx.fillText(line, cx, y);
  });
}

/**
 * Draw centered text with optional outline. (kept for backward compat — single line only)
 */
function drawText(ctx, text, x, y, opts = {}) {
  const { font = 'bold 40px Inter, system-ui', color = '#FFFFFF', strokeColor = '#000000', strokeWidth = 5, align = 'center' } = opts;
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  if (strokeWidth > 0) {
    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeColor;
    ctx.strokeText(text, x, y);
  }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

/**
 * Hearts emoji floating up.
 */
function drawHearts(ctx, w, h, particles, t) {
  ctx.font = '32px serif';
  particles.forEach((p) => {
    const py = h - ((t * p.speed + p.offset) % (h + 60)) + 30;
    const px = p.x + Math.sin((t + p.offset) * 0.1) * p.sway;
    ctx.globalAlpha = p.alpha;
    ctx.fillText(p.emoji, px, py);
  });
  ctx.globalAlpha = 1;
}

function drawConfetti(ctx, w, h, particles, t) {
  particles.forEach((p) => {
    const py = ((t * p.speed + p.offset) % (h + 40)) - 20;
    const px = p.x + Math.sin((t + p.offset) * 0.15) * p.sway;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(((t + p.offset) * p.spin) % (Math.PI * 2));
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
    ctx.restore();
  });
}

function drawSparkles(ctx, w, h, particles, t) {
  particles.forEach((p) => {
    const blink = (Math.sin((t + p.offset) * 0.3) + 1) / 2;
    if (blink < 0.2) return;
    ctx.globalAlpha = blink;
    ctx.font = `${p.size}px serif`;
    ctx.fillStyle = p.color;
    ctx.fillText('✦', p.x, p.y);
  });
  ctx.globalAlpha = 1;
}

/**
 * Main entry: builds a GIF as a Blob.
 *
 * options:
 *  - imageSrc: dataURL or URL of base image (optional for 'color-bg' effect)
 *  - effect: 'text', 'hearts', 'confetti', 'sparkles', 'zoom', 'color-bg'
 *  - text: optional overlay text
 *  - textColor, strokeColor
 *  - size: output size in px (square). Default 320.
 *  - frames: number of frames. Default 30.
 *  - fps: frames per second. Default 12.
 *  - onProgress: callback(progress: 0..1)
 */
export async function buildGif({
  imageSrc = null,
  effect = 'text',
  text = '',
  textColor = '#FFFFFF',
  strokeColor = '#000000',
  bgColor = '#FFFFFF', // Used by 'solid-bg' effect
  size = 320,
  frames = 30,
  fps = 12,
  stickerMode = false,
  onProgress = () => {},
} = {}) {
  const img = imageSrc ? await loadImg(imageSrc) : null;
  const w = size;
  const h = size;
  const delay = Math.round(1000 / fps);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  // Pre-build particle systems for some effects
  let hearts = [];
  let confetti = [];
  let sparkles = [];
  if (effect === 'hearts') {
    const emojis = ['❤️', '💖', '💕', '💗'];
    hearts = Array.from({ length: 12 }, () => ({
      x: Math.random() * w,
      offset: Math.random() * h,
      speed: 3 + Math.random() * 4,
      sway: 10 + Math.random() * 20,
      alpha: 0.6 + Math.random() * 0.4,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
    }));
  }
  if (effect === 'confetti') {
    const colors = ['#ff3b6b', '#ffd166', '#06d6a0', '#118ab2', '#7c3aed', '#ef4444', '#22c55e'];
    confetti = Array.from({ length: 40 }, () => ({
      x: Math.random() * w,
      offset: Math.random() * h,
      speed: 4 + Math.random() * 6,
      sway: 15 + Math.random() * 30,
      size: 8 + Math.random() * 8,
      spin: Math.random() * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }
  if (effect === 'sparkles') {
    const colors = ['#FFFFFF', '#FFE066', '#FFB3D9', '#A0E7E5'];
    sparkles = Array.from({ length: 18 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      offset: Math.random() * 20,
      size: 14 + Math.random() * 18,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }

  const gif = GIFEncoder();

  for (let f = 0; f < frames; f++) {
    const t = f;
    const progress = f / frames; // 0..1
    ctx.clearRect(0, 0, w, h);

    if (stickerMode) {
      // Transparent background — only render the cutout image + effects
      if (effect === 'zoom') {
        const scale = 1 + Math.sin(progress * Math.PI * 2) * 0.06;
        if (img) drawContain(ctx, img, w, h, scale);
      } else if (img) {
        drawContain(ctx, img, w, h, 1);
      }
    } else if (effect === 'solid-bg') {
      // Solid background with optional image overlay — text is the focus, animation is a subtle pulse
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, w, h);
      if (img) {
        const scale = 1 + Math.sin(progress * Math.PI * 2) * 0.03;
        drawContain(ctx, img, w, h, 0.78 * scale);
      }
    } else if (effect === 'color-bg') {
      // Animated rainbow gradient background
      const hue1 = (t * 12) % 360;
      const hue2 = (hue1 + 120) % 360;
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, `hsl(${hue1}, 85%, 55%)`);
      grad.addColorStop(1, `hsl(${hue2}, 85%, 55%)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      if (img) drawContain(ctx, img, w, h, 0.8);
    } else if (effect === 'zoom') {
      // Pulse effect (subtle breathing)
      const scale = 1 + Math.sin(progress * Math.PI * 2) * 0.06;
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, w, h);
      if (img) drawContain(ctx, img, w, h, scale);
    } else {
      // Default: draw image as background (full bleed cover for nicer look)
      if (img) {
        const ratio = img.width / img.height;
        let dw, dh;
        if (ratio > 1) {
          dh = h;
          dw = h * ratio;
        } else {
          dw = w;
          dh = w / ratio;
        }
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      } else {
        // No image → use animated gradient
        const hue = (t * 12) % 360;
        ctx.fillStyle = `hsl(${hue}, 80%, 50%)`;
        ctx.fillRect(0, 0, w, h);
      }
    }

    // Overlay effect particles
    if (effect === 'hearts') drawHearts(ctx, w, h, hearts, t);
    if (effect === 'confetti') drawConfetti(ctx, w, h, confetti, t);
    if (effect === 'sparkles') drawSparkles(ctx, w, h, sparkles, t);

    // Text overlay (typewriter for 'text', static fitted text for others)
    if (text) {
      let visibleText = text;
      if (effect === 'text') {
        // Total visible characters increases over time, but we still wrap & fit
        const totalChars = text.length;
        const charsToShow = Math.min(totalChars, Math.floor((progress + 0.1) * totalChars * 1.2));
        visibleText = text.slice(0, charsToShow);
      }

      // Define text box (padded inside canvas). For most effects use full height; for 'solid-bg' use a centered box.
      const padX = Math.round(w * 0.08);
      const boxW = w - padX * 2;
      let boxH, cy;
      if (effect === 'solid-bg' || effect === 'color-bg' || stickerMode) {
        // Full vertical centered box
        boxH = Math.round(h * 0.78);
        cy = h / 2;
      } else {
        // Bottom-third box (above image bottom)
        boxH = Math.round(h * 0.30);
        cy = Math.round(h * 0.82);
      }

      drawFittedText(ctx, visibleText, w / 2, cy, boxW, boxH, {
        color: textColor,
        strokeColor,
        maxFont: Math.round(size * 0.22),
        minFont: 10,
      });
    }

    // Encode frame
    const data = getFrameData(ctx, w, h);
    if (stickerMode) {
      // Build palette + reserve index 0 as transparent
      // Use rgba4444 to keep alpha in quantization, then mark fully-transparent pixels
      const palette = quantize(data, 255, { format: 'rgba4444' });
      // Ensure first palette entry is "magic" transparent placeholder
      const tPalette = [[0, 0, 0, 0], ...palette];
      const index = applyPalette(data, tPalette, 'rgba4444');
      // Force index 0 wherever original alpha is < 128
      for (let i = 0; i < index.length; i++) {
        if (data[i * 4 + 3] < 128) index[i] = 0;
      }
      gif.writeFrame(index, w, h, {
        palette: tPalette,
        delay,
        transparent: true,
        transparentIndex: 0,
        dispose: 2, // restore to background (clears each frame)
      });
    } else {
      const palette = quantize(data, 256);
      const index = applyPalette(data, palette);
      gif.writeFrame(index, w, h, { palette, delay });
    }
    onProgress((f + 1) / frames);

    // Yield to UI thread occasionally
    if (f % 5 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  gif.finish();
  return new Blob([gif.bytes()], { type: 'image/gif' });
}
